import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { SystemEnvironment } from '@mono-repo/data';
import { overrideSingleConsentTranslations, overrideDocuments2Translations } from '@mono-repo/web-components';
import { AppModule } from './app/app.module';
import { environment } from './environments_mc/environment';
import { overrideTranslations } from './translations/override';
// eslint-disable-next-line @nx/enforce-module-boundaries

if (environment.production) {
  enableProdMode();
}

if (environment.cookieConsent?.CookieBot) {
  // Cookiebot must be loaded BEFORE any other scripts that set cookies
  const cookieHeadNode = document.createElement('script');
  cookieHeadNode.src = 'https://consent.cookiebot.com/uc.js';
  cookieHeadNode.type = 'text/javascript';
  cookieHeadNode.id = 'Cookiebot';
  cookieHeadNode.setAttribute('data-cbid', environment.cookieConsent.CookieBot.cbid);
  cookieHeadNode.setAttribute('data-blockingmode', 'auto');
  cookieHeadNode.async = true;
  document.head.children[environment.cookieConsent.CookieBot.headerPosition].before(cookieHeadNode);
}

if (environment.analyticsTool) {
  const analyticsScript = document.createElement('script');
  analyticsScript.id = 'analyticsScript';
  // this additional attribute is for the cookiebot. It enables that the cookiebot blocks all matomo requests as long as
  // no consent on the cookies was given
  if (environment.cookieConsent?.CookieBot) {
    analyticsScript.setAttribute('type', 'text/plain');
    analyticsScript.setAttribute('data-cookieconsent', 'statistics');
  } else {
    analyticsScript.setAttribute('type', 'text/javascript');
  }

  switch (environment.analyticsTool.tool) {
    case 'matomo':
      // The following snipped has been taking from the Setup Documentation for Matomo inside AngularTics: https://github.com/angulartics/angulartics2/tree/master/src/lib/providers/matomo#setup
      analyticsScript.innerHTML = `
			var _paq = _paq || [];
			/* tracker methods like "setCustomDimension" should be called before "trackPageView" */
      `;
      if (environment.systemEnvironment === SystemEnvironment.APP) {
        analyticsScript.innerHTML += `
        _paq.push(['requireConsent']);
        _paq.push(['enableFileTracking']);
        `;
      }
      analyticsScript.innerHTML += `
      _paq.push(['enableLinkTracking']);
			(function() {
			  var u="${environment.analyticsTool.domain}";
        _paq.push(['setTrackerUrl', u+'matomo.php']);
			  _paq.push(['setSiteId', '${environment.analyticsTool.siteId}']);
			  var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
			  g.type='text/javascript'; g.async=true; g.defer=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
			  s.parentNode.insertBefore(g,s);
			})();`;
      if (environment.analyticsTool.containerId) {
        analyticsScript.innerHTML += `
        var _mtm = window._mtm = window._mtm || [];
        _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
        (function() {
          var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
          g.async=true; g.src='https://analytics.gewobag.de/js/container_VgByxkWh_staging_260b656bcadf8efc9554b7e3.js'; s.parentNode.insertBefore(g,s);
        })();
        `;
      }
      break;
    case 'googleAnalytics': {
      const analyticsGoogletagmanagerScript = document.createElement('script');
      analyticsGoogletagmanagerScript.setAttribute('async', '');
      analyticsGoogletagmanagerScript.setAttribute(
        'src',
        `https://www.googletagmanager.com/gtag/js?id=${environment.analyticsTool.id}`,
      );
      document.body.appendChild(analyticsGoogletagmanagerScript);

      analyticsScript.innerHTML = `
			window.dataLayer = window.dataLayer || [];
        	function gtag(){dataLayer.push(arguments);}
        	gtag('js', new Date());
        	gtag('config', '${environment.analyticsTool.id}');`;
      break;
    }
  }

  document.body.appendChild(analyticsScript);
}

declare const wkWebView;
declare const device;
const bootstrapAngularApp = () => {
  if (environment.systemEnvironment === SystemEnvironment.APP && device.platform === 'iOS') {
    wkWebView.injectCookie(
      `mobile-${environment.scpId}.dispatcher.${environment.appConfiguration.serverRegion}.hana.ondemand.com/`,
    );
  }
  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch((err) => console.error(err));
};

overrideTranslations();
overrideSingleConsentTranslations();
overrideDocuments2Translations();

const baseHrefElement = document.getElementsByTagName('base')[0];
baseHrefElement.setAttribute('href', document.location as unknown as string);

if (environment.systemEnvironment === SystemEnvironment.APP) {
  const monkeyPatchingScript = document.createElement('script');
  monkeyPatchingScript.innerHTML = `
      // monkey-patching event listeners for cordova to run in angular zone (for router animation on native 'browser.back()'
      window.addEventListener = function () {
        EventTarget.prototype.addEventListener.apply(this, arguments);
      };
      window.removeEventListener = function () {
        EventTarget.prototype.removeEventListener.apply(this, arguments);
      };
      document.addEventListener = function () {
        EventTarget.prototype.addEventListener.apply(this, arguments);
      };
      document.removeEventListener = function () {
        EventTarget.prototype.removeEventListener.apply(this, arguments);
      };`;

  const cordovaScript = document.createElement('script');
  cordovaScript.setAttribute('type', 'text/javascript');
  cordovaScript.setAttribute('src', 'cordova.js');

  document.body.appendChild(monkeyPatchingScript);
  document.body.appendChild(cordovaScript);

  document.addEventListener('deviceready', bootstrapAngularApp, false);
} else {
  bootstrapAngularApp();
}
